import { motion } from "framer-motion";
import { Globe2, MapPin, ArrowRight, Globe, Check } from "lucide-react";
import { useJourney } from "../../context/JourneyContext";
import { useLang } from "../../i18n/LanguageContext";
import { LogoMark } from "./LogoMark";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../ui/dropdown-menu";

export const SplashGate = () => {
  const { setJourney } = useJourney();
  const { t, lang, setLang, langs } = useLang();
  const g = t.gate;
  const PANELS = [
    { key: "foreign", icon: Globe2, ...g.foreign },
    { key: "local", icon: MapPin, ...g.local },
  ];

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-[100] bg-[var(--page)] text-[var(--ink)] overflow-y-auto"
      data-testid="splash-gate"
    >
      <div className="min-h-full flex flex-col">
        {/* top bar */}
        <div className="border-b border-[var(--hairline)]">
          <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-5 flex items-center justify-between gap-2.5">
            <div className="flex items-center gap-2.5">
              <LogoMark className="h-9 w-auto shrink-0" testId="gate-logo-mark" />
              <span className="font-display text-lg sm:text-xl leading-none tracking-tight">Alvin Kumar &amp; Associates</span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="gate-language-switcher"
                  aria-label="Change language"
                  className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-[3px] border border-[var(--hairline)]
                             text-[var(--ink)] text-xs font-semibold transition-colors hover:bg-[var(--ink)]/5"
                >
                  <Globe className="h-4 w-4" strokeWidth={1.8} />
                  <span className="hidden sm:inline">{langs.find((l) => l.code === lang)?.label}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-[70vh] overflow-y-auto z-[110]">
                {langs.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`gate-lang-option-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className="flex items-center justify-between gap-6 cursor-pointer"
                  >
                    <span>{l.label}</span>
                    {lang === l.code && <Check className="h-4 w-4" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="mx-auto max-w-[1400px] w-full px-5 sm:px-8 py-14 sm:py-20 flex-1 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[var(--ink)]" />
              <span className="text-[11px] sm:text-xs uppercase tracking-[0.25em] text-[var(--muted-ink)] font-semibold">
                {g.startHere}
              </span>
            </div>
            <h1 className="font-display font-semibold tracking-tight leading-[1.03] text-4xl sm:text-6xl max-w-4xl">
              {g.title}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[var(--muted-ink)] max-w-2xl">
              {g.sub}
            </p>
          </motion.div>

          <div className="mt-12 sm:mt-16 grid md:grid-cols-2 border-t border-l border-[var(--hairline)]">
            {PANELS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.button
                  key={p.key}
                  data-testid={`gate-${p.key}`}
                  onClick={() => setJourney(p.key)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
                  className="group relative text-left p-8 sm:p-12 border-r border-b border-[var(--hairline)]
                             transition-colors duration-300 hover:bg-[var(--ink)] hover:text-[var(--page)]"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-[0.25em] font-semibold opacity-60">{p.kicker}</span>
                    <Icon className="h-6 w-6 opacity-70" strokeWidth={1.6} />
                  </div>
                  <h2 className="mt-8 font-display text-2xl sm:text-4xl leading-tight">{p.title}</h2>
                  <p className="mt-4 text-sm sm:text-base opacity-70 leading-relaxed max-w-sm">{p.desc}</p>
                  <div className="mt-8 flex items-center justify-between">
                    <span className="inline-block border border-current/30 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide opacity-80">
                      {p.badge}
                    </span>
                    <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
                      {g.continue} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
